/**
 * sendWhatsApp — helper universal para enviar WhatsApp de qualquer tela.
 *
 * Comportamento:
 *   1. Tenta POST /api/tenant/whatsapp/send (usa provider configurado)
 *   2. Se ok: mostra toast "Enviado!"
 *   3. Se falhou / não configurado: abre link wa.me automaticamente (fallback)
 *
 * Uso:
 *   import { sendWhatsApp } from "@/lib/whatsapp";
 *   await sendWhatsApp({ phone: "11987654321", text: "Olá", context: "risk_manager" });
 */
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * Abre uma URL preservando o gesto do usuário (para não ser bloqueado no mobile).
 * Se `preopenedWindow` foi criado sincronicamente no click, redireciona ele.
 * Caso contrário, tenta window.open normal e, se bloqueado, navega na aba atual
 * (o wa.me abre o app do WhatsApp mesmo em same-tab via universal link).
 */
function openWaLink(url, preopenedWindow) {
  if (preopenedWindow && !preopenedWindow.closed) {
    try {
      preopenedWindow.location.href = url;
      return;
    } catch (_) { /* fallthrough */ }
  }
  const w = window.open(url, "_blank");
  if (!w || w.closed || typeof w.closed === "undefined") {
    // popup bloqueado (comum em Safari/Chrome mobile) → navega na aba atual
    window.location.href = url;
  }
}

export async function sendWhatsApp({ phone, text, clientId, context }) {
  if (!phone) {
    toast.error("Cliente sem WhatsApp cadastrado");
    return { ok: false };
  }
  // Pré-abre a janela SINCRONAMENTE dentro do click para preservar o gesto
  // do usuário. Mobile browsers bloqueiam window.open() após await.
  // Se o envio automático der certo, fechamos essa janela.
  let preopened = null;
  try { preopened = window.open("", "_blank"); } catch (_) { preopened = null; }
  try {
    const { data } = await api.post("/tenant/whatsapp/send", {
      phone,
      text,
      client_id: clientId,
      context,
    });
    if (data.ok) {
      // envio automático deu certo — não precisa da janela pré-aberta
      if (preopened && !preopened.closed) { try { preopened.close(); } catch (_e1) { /* noop */ } }
      toast.success("Mensagem enviada via WhatsApp!");
      return { ok: true, ...data };
    }
    // fallback → abre wa.me
    if (data.wa_link) {
      openWaLink(data.wa_link, preopened);
      if (data.provider === "wa_link") {
        toast.info("Envio manual: abrindo WhatsApp Web/App…");
      } else {
        toast.warning(
          `Falha no envio automático (${data.error || "erro"}). Abrindo wa.me como fallback.`
        );
      }
    } else if (preopened && !preopened.closed) {
      try { preopened.close(); } catch (_e2) { /* noop */ }
    }
    return { ok: false, ...data };
  } catch (e) {
    // rede caiu ou erro inesperado — fallback wa.me local
    const digits = String(phone).replace(/\D/g, "");
    const finalPhone = digits.length >= 12 && digits.startsWith("55") ? digits : "55" + digits;
    const url = `https://wa.me/${finalPhone}?text=${encodeURIComponent(text)}`;
    openWaLink(url, preopened);
    toast.warning("Falha ao contatar servidor. Abrindo wa.me diretamente.");
    return { ok: false, fallback: true };
  }
}
